import type { FormOp } from '@/pages/forms/lib/apply-form-ops'

import { getAssistantSurface } from './assistant-actions'

/** Matches the server tool's `maxItems`. */
const MAX_OPS = 50
/** Matches the server's cap on a form id in the page context. */
const MAX_ID = 64

const NOT_READY = 'The builder is still loading; try again in a moment.'
const NOT_OPEN = 'Open the form in the builder first (show_form view builder).'
const APPLIED = 'Applied in the builder; the user saves with Save.'

type Check = (op: Record<string, unknown>) => string | null

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const isName = (v: unknown): boolean => typeof v === 'string' && v.trim() !== ''
const isAfter = (v: unknown): boolean => v === null || isName(v)

const needName: Check = op => (isName(op.name) ? null : 'needs the question `name`')

/** What each builder op needs, so a malformed one is refused here instead of throwing in the builder. */
const CHECKS: Record<FormOp['op'], Check> = {
  add_field: op => {
    const f = op.field

    if (!isObject(f) || !isName(f.name) || !isName(f.label) || !isName(f.type)) {
      return 'needs `field` with name, label and type'
    }

    return op.after === undefined || isAfter(op.after) ? null : '`after` must be a question name or null'
  },
  update_field: op => needName(op) ?? (isObject(op.patch) ? null : 'needs a `patch` object'),
  remove_field: needName,
  move_field: op => needName(op) ?? (isAfter(op.after) ? null : 'needs `after`: a question name, or null for first'),
  set_rules: op => needName(op) ?? (Array.isArray(op.rules) ? null : 'needs `rules` as a list ([] clears)'),
  set_details: op => {
    if (op.name !== undefined && typeof op.name !== 'string') return '`name` (the form title) must be text'

    return op.description === undefined || typeof op.description === 'string' ? null : '`description` must be text'
  },
}

/** A choice as the Forms schema stores it: a bare string is its own label (as `create_form` does). */
function toOption(o: unknown): unknown {
  if (typeof o === 'string' || typeof o === 'number') return { value: String(o), label: String(o) }
  if (!isObject(o)) return o

  const value = o.value === undefined || o.value === null ? '' : String(o.value)
  const label = o.label === undefined || o.label === null || o.label === '' ? value : String(o.label)

  return { ...o, value, label }
}

/** `options` as `{value, label}` and `help_text` folded into `helpText` (an explicit `helpText` wins). */
function normaliseField(f: Record<string, unknown>): Record<string, unknown> {
  const { help_text: alias, ...rest } = f
  const out: Record<string, unknown> = { ...rest }

  if (Array.isArray(out.options)) out.options = out.options.map(toOption)
  if (alias !== undefined && out.helpText === undefined) out.helpText = alias

  return out
}

function normaliseOp(op: FormOp): FormOp {
  const o = op as unknown as Record<string, unknown>

  if (o.op === 'add_field' && isObject(o.field)) return { ...o, field: normaliseField(o.field) } as unknown as FormOp
  if (o.op === 'update_field' && isObject(o.patch)) return { ...o, patch: normaliseField(o.patch) } as unknown as FormOp

  return op
}

const invalid = (message: string) => ({ success: false, error: 'invalid_input', message })

function readOps(raw: unknown): FormOp[] | { message: string } {
  if (!Array.isArray(raw) || raw.length === 0) return { message: '`ops` must be a non-empty list of edits.' }
  if (raw.length > MAX_OPS) return { message: `At most ${MAX_OPS} ops in one edit_form call.` }

  for (const [i, op] of raw.entries()) {
    if (!isObject(op)) return { message: `ops[${i}] must be an object with an \`op\`.` }

    const kind = op.op
    const check = typeof kind === 'string' && Object.prototype.hasOwnProperty.call(CHECKS, kind) ? CHECKS[kind as FormOp['op']] : null
    if (!check) return { message: `ops[${i}]: unknown op "${String(kind)}".` }

    const problem = check(op)
    if (problem) return { message: `ops[${i}] (${kind}) ${problem}.` }
  }

  return (raw as FormOp[]).map(normaliseOp)
}

/**
 * `edit_form`: applies a batch of edits to the form open in the builder, through the builder
 * surface's `applyEdit` (all or nothing; never saves). Any other page, or another form, is `not_open`.
 */
export async function executeEditForm(input: unknown, deps?: { currentRoute?: () => string }): Promise<unknown> {
  const raw = (input ?? {}) as { form_id?: unknown; ops?: unknown }
  const formId = typeof raw.form_id === 'string' ? raw.form_id.trim() : ''

  if (!formId || formId.length > MAX_ID) return invalid('`form_id` is required.')

  const ops = readOps(raw.ops)
  if ('message' in ops) return invalid(ops.message)

  const handle = getAssistantSurface()

  if (handle?.kind !== 'forms' || !handle.applyEdit) {
    // On the builder route but not wired yet: the page is loading, not closed.
    const route = deps?.currentRoute?.() ?? ''

    if (/^\/forms\/builder\/[^/?#]+/.test(route)) return { error: 'not_ready', message: NOT_READY }

    return { error: 'not_open', message: NOT_OPEN }
  }

  const result = handle.applyEdit(formId, ops)

  if (result.ok) return { success: true, form_id: formId, changed: result.changed, message: APPLIED }
  if (result.error === 'not_open') return { success: false, form_id: formId, error: 'not_open', message: NOT_OPEN }

  return { success: false, form_id: formId, error: result.error, ...(result.issues ? { issues: result.issues } : {}) }
}
